import { Link } from "react-router-dom";
import { StatusPill, type DeploymentStatus } from "@/components/StatusPill";
import { EnvBadge, type Env } from "@/components/EnvBadge";
import { CommitRef } from "@/components/CommitRef";
import { RelativeTime } from "@/components/RelativeTime";
import { duration } from "@/lib/format";

export interface RecentDeployment {
  id: string;
  status: DeploymentStatus;
  env: Env;
  branch: string;
  commitSha: string;
  commitMessage: string | null;
  commitAuthor: string | null;
  envResolvedFrom: string;
  createdAt: string;
  readyAt: string | null;
  durationMs: number | null;
}

interface Props {
  orgSlug: string;
  projectSlug: string;
  deployments: RecentDeployment[];
}

/**
 * v4 (004-project-overview-tab, US4 / Phase 6) — Recent Deployments list.
 *
 * Renders the last 3 deployments across all envs for the project (the
 * `/overview` endpoint already enforces the limit and ordering). Whole row
 * is clickable → navigates to the deployment detail page. Hidden by the
 * parent when the array is empty.
 *
 * Style mirrors `pages/Deployments.tsx` row layout so the visual language
 * is consistent across the two pages.
 */
export function RecentDeployments({ orgSlug, projectSlug, deployments }: Props) {
  if (deployments.length === 0) return null;

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold">Recent deployments</h3>
        <Link
          to={`/orgs/${orgSlug}/projects/${projectSlug}/deployments`}
          className="text-2xs text-subtle-foreground hover:text-foreground"
        >
          View all →
        </Link>
      </div>
      {deployments.map((d) => {
        const trailing = d.commitAuthor ?? d.envResolvedFrom;
        return (
          <Link
            key={d.id}
            to={`/orgs/${orgSlug}/projects/${projectSlug}/deployments/${d.id}`}
            className="of-row flex-wrap"
          >
            <StatusPill status={d.status} />
            <EnvBadge env={d.env} />
            <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
              <CommitRef branch={d.branch} sha={d.commitSha} message={d.commitMessage} />
            </div>
            <div className="hidden md:block text-xs text-muted-foreground tabular-nums w-12 text-right">
              {duration(d.durationMs)}
            </div>
            <div
              className="hidden md:block text-xs text-muted-foreground font-mono truncate max-w-[140px]"
              title={trailing}
            >
              {trailing}
            </div>
            <RelativeTime value={d.readyAt ?? d.createdAt} />
          </Link>
        );
      })}
    </div>
  );
}
